/**
 * Thin plumbing for kiosk display assignment + controller→display state sync.
 * Phase 3 UI can call getKioskRole() and branch rendering; no UI wired here yet.
 */

export type KioskRole = 'controller' | 'display' | 'regular';

const ROLE_KEY = 'ltsKioskRole';

function stateBaseUrl(): string {
  const fromEnv =
    typeof import.meta !== 'undefined'
      ? String((import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_KIOSK_STATE_URL || '').trim()
      : '';
  return (fromEnv || 'http://127.0.0.1:18765').replace(/\/+$/, '');
}

/**
 * Chromium Local Network Access: mark agent calls as loopback so HTTPS portal
 * pages are not treated as mixed content / unknown address space.
 */
function loopbackFetchInit(init: RequestInit = {}): RequestInit {
  return {
    ...init,
    // Not yet on all TS DOM libs; Chromium/Brave honor this at runtime.
    ...({ targetAddressSpace: 'loopback' } as object),
  } as RequestInit
}

/** Base URL for the local kiosk-linux host agent (loopback). */
export function getKioskAgentBaseUrl(): string {
  return stateBaseUrl();
}

export type KioskAgentHealth = {
  ok: boolean
  service?: string
  version?: string
  startedAt?: string
  topology?: string | null
  monitorCount?: number
  launchedBrowsers?: Array<{
    name?: string
    role?: string
    pid?: number
    monitor?: string | null
  }>
  config?: {
    deviceAuthEnabled?: boolean
    portalBaseUrl?: string | null
    launchBrowserOnStart?: boolean
  }
  machine?: {
    hostname?: string
  }
}

/**
 * Probe the local host agent. Throws if unreachable or non-OK.
 */
export async function fetchKioskAgentHealth(timeoutMs = 2500): Promise<KioskAgentHealth> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer =
    ctrl && timeoutMs > 0
      ? window.setTimeout(() => ctrl.abort(), timeoutMs)
      : null
  try {
    const res = await fetch(
      `${stateBaseUrl()}/health`,
      loopbackFetchInit({
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: ctrl?.signal,
      }),
    )
    const data = (await res.json().catch(() => ({}))) as KioskAgentHealth
    if (!res.ok || data?.ok === false) {
      throw new Error((data as { message?: string })?.message || `HTTP ${res.status}`)
    }
    return data
  } finally {
    if (timer != null) window.clearTimeout(timer)
  }
}

function normalizeRole(raw: unknown): KioskRole | null {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'controller' || v === 'display' || v === 'regular') return v;
  return null;
}

/** Persist role for this browser profile (session-scoped). */
export function setKioskRole(role: KioskRole | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!role) {
      sessionStorage.removeItem(ROLE_KEY);
    } else {
      sessionStorage.setItem(ROLE_KEY, role);
    }
    window.dispatchEvent(new Event('lts-kiosk-role-change'));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getKioskRole(): KioskRole | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeRole(sessionStorage.getItem(ROLE_KEY));
  } catch {
    return null;
  }
}

/**
 * Read ?kioskRole= from the URL, persist it, and strip the query param.
 * Safe to call on every boot; no-ops when param absent.
 */
export function consumeKioskRoleQuery(): KioskRole | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const role = normalizeRole(url.searchParams.get('kioskRole'));
    if (!role) return null;
    setKioskRole(role);
    url.searchParams.delete('kioskRole');
    window.history.replaceState(null, '', url.toString());
    return role;
  } catch {
    return null;
  }
}

export type KioskSharedState = {
  version?: number;
  view?: string | null;
  payload?: Record<string, unknown>;
  [key: string]: unknown;
};

export type KioskStateEnvelope = {
  ok: boolean;
  state: KioskSharedState;
  updatedAt: string | null;
  updatedBy: string | null;
};

/** Soft-fail GET — returns null when not on a kiosk host. */
export async function getKioskState(): Promise<KioskStateEnvelope | null> {
  try {
    const res = await fetch(
      `${stateBaseUrl()}/kiosk/state`,
      loopbackFetchInit({
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
    );
    if (!res.ok) return null;
    const data = (await res.json()) as KioskStateEnvelope;
    return data?.ok ? data : null;
  } catch {
    return null;
  }
}

/**
 * Soft-fail PUT. Prefer calling from controller (or regular) roles.
 * Body may be the state object, or `{ state, updatedBy }`.
 */
export async function putKioskState(
  state: KioskSharedState,
  updatedBy?: string | null
): Promise<KioskStateEnvelope | null> {
  try {
    const body =
      updatedBy != null && String(updatedBy).trim()
        ? { state, updatedBy: String(updatedBy).trim() }
        : state;
    const res = await fetch(
      `${stateBaseUrl()}/kiosk/state`,
      loopbackFetchInit({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }),
    );
    if (!res.ok) return null;
    const data = (await res.json()) as KioskStateEnvelope;
    return data?.ok ? data : null;
  } catch {
    return null;
  }
}

/**
 * Merge keys into the shared payload without wiping unrelated fields
 * (route sync, UI scale, Control Center, refresh, etc.).
 */
export async function patchKioskPayload(
  patch: Record<string, unknown>,
  updatedBy?: string | null
): Promise<KioskStateEnvelope | null> {
  const current = await getKioskState();
  const prevState = current?.state && typeof current.state === 'object' ? current.state : {};
  const prevPayload =
    prevState.payload && typeof prevState.payload === 'object' && !Array.isArray(prevState.payload)
      ? (prevState.payload as Record<string, unknown>)
      : {};
  return putKioskState(
    {
      ...prevState,
      version: typeof prevState.version === 'number' ? prevState.version : 1,
      view: prevState.view ?? 'route',
      payload: {
        ...prevPayload,
        ...patch,
      },
    },
    updatedBy ?? null
  );
}

/**
 * Ask the display browser to reload (via SSE), preserving existing shared payload.
 * Soft-fails if the local host agent is unreachable.
 */
export async function requestKioskDisplayRefresh(updatedBy?: string | null): Promise<void> {
  await patchKioskPayload({ refreshAt: Date.now() }, updatedBy ?? 'controller');
}

/**
 * Ask browsers to hard-reload (clear Cache Storage, cache-bust navigation).
 */
export async function requestKioskDisplayHardRefresh(updatedBy?: string | null): Promise<void> {
  await patchKioskPayload({ hardRefreshAt: Date.now() }, updatedBy ?? 'controller');
}

async function postKioskSystem(path: string): Promise<{ ok: boolean; message?: string }> {
  const res = await fetch(
    `${stateBaseUrl()}${path}`,
    loopbackFetchInit({
      method: 'POST',
      headers: { Accept: 'application/json' },
    }),
  );
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
  if (!res.ok || data?.ok === false) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }
  return { ok: true, message: data?.message };
}

/** Stop agent → git update → start (detached on host). */
export async function requestKioskAgentRestart(): Promise<void> {
  await postKioskSystem('/system/restart-agent');
}

/** Reboot the kiosk machine (detached on host). */
export async function requestKioskDeviceReboot(): Promise<void> {
  await postKioskSystem('/system/reboot');
}

/** Soft reload current document. */
export function kioskSoftReload(): void {
  window.location.reload();
}

/** Clear Cache Storage and force a cache-busted navigation. */
export async function kioskHardReload(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_kiosk_bust', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Subscribe to SSE state updates. Returns an unsubscribe function.
 * Soft-fails (no-op unsubscribe) when EventSource is unavailable.
 */
export function subscribeKioskState(cb: (envelope: KioskStateEnvelope) => void): () => void {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
    return () => {};
  }
  let es: EventSource | null = null;
  try {
    es = new EventSource(`${stateBaseUrl()}/kiosk/state/stream`);
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(String(event.data || '')) as KioskStateEnvelope;
        if (data?.ok) cb(data);
      } catch {
        /* ignore malformed */
      }
    };
    es.onerror = () => {
      /* browser will retry; leave soft */
    };
  } catch {
    return () => {};
  }
  return () => {
    try {
      es?.close();
    } catch {
      /* ignore */
    }
  };
}
